/**
 * InclusionsExclusions Component
 * 
 * Displays inclusions and exclusions lists side by side.
 * Supports multiple sources for DMC packages (package-specific + tenant defaults)
 * 
 * Usage:
 *   <InclusionsExclusions
 *     inclusions={termsData.inclusions}
 *     exclusions={termsData.exclusions}
 *     dmcInclusions={dmcParsed.inclusions}
 *     dmcExclusions={dmcParsed.exclusions}
 *     packageType={quotation.route_type}
 *     variant="detail"
 *   />
 */

import React from 'react';
import { Check, X, Package } from 'lucide-react';
import { COMPONENT_VARIANTS } from './displayConfig';

const InclusionsExclusions = ({ 
  inclusions = '',
  exclusions = '',
  dmcInclusions = '',
  dmcExclusions = '',
  packageType = 'custom',
  variant = 'detail',
  className = ''
}) => {
  // Parse string to array if needed
  const parseList = (input) => {
    if (!input) return [];
    if (Array.isArray(input)) return input.filter(Boolean);
    return input.split('\n').filter(Boolean);
  };
  
  const inclusionsList = parseList(inclusions);
  const exclusionsList = parseList(exclusions);
  const dmcInclusionsList = parseList(dmcInclusions);
  const dmcExclusionsList = parseList(dmcExclusions);
  
  const isDMCPackage = packageType === 'dmc_package';
  const hasDMCInclusions = isDMCPackage && dmcInclusionsList.length > 0;
  const hasDMCExclusions = isDMCPackage && dmcExclusionsList.length > 0;
  
  // For DMC packages, show both DMC-specific AND tenant defaults
  // For other packages, show only tenant/quotation settings
  
  if (inclusionsList.length === 0 && exclusionsList.length === 0 && 
      dmcInclusionsList.length === 0 && dmcExclusionsList.length === 0) {
    return null;
  }

  const styles = COMPONENT_VARIANTS[variant] || COMPONENT_VARIANTS.detail;

  return (
    <div 
      className={`space-y-4 ${className}`}
      data-package-type={packageType}
      data-component="inclusions-exclusions"
      data-testid="inclusions-exclusions"
    >
      {/* DMC Package-Specific Inclusions/Exclusions */}
      {isDMCPackage && (hasDMCInclusions || hasDMCExclusions) && (
        <div>
          <h4 className="text-sm font-semibold text-[var(--navy)] mb-2 flex items-center gap-2">
            <Package className="w-4 h-4" />
            Package Inclusions & Exclusions
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* DMC Inclusions */}
            {hasDMCInclusions && (
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                <div className="px-4 py-2 font-semibold flex items-center gap-2 text-[var(--navy)] border-b border-gray-200">
                  <Check className="w-4 h-4 text-success" />
                  Package Inclusions
                </div>
                <ul className={`p-4 space-y-2 ${styles.fontSize}`}>
                  {dmcInclusionsList.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-700">
                      <Check className="w-4 h-4 text-success mt-0.5 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* DMC Exclusions */}
            {hasDMCExclusions && (
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                <div className="px-4 py-2 font-semibold flex items-center gap-2 text-[var(--navy)] border-b border-gray-200">
                  <X className="w-4 h-4 text-error" />
                  Package Exclusions
                </div>
                <ul className={`p-4 space-y-2 ${styles.fontSize}`}>
                  {dmcExclusionsList.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-700">
                      <X className="w-4 h-4 text-error mt-0.5 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Standard/Tenant Default Inclusions/Exclusions */}
      {(inclusionsList.length > 0 || exclusionsList.length > 0) && (
        <div>
          {isDMCPackage && (hasDMCInclusions || hasDMCExclusions) && (
            <h4 className="text-sm font-semibold text-gray-600 mb-2">
              Standard Terms
            </h4>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Inclusions */}
            {inclusionsList.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                <div className="px-4 py-2 font-semibold flex items-center gap-2 text-[var(--navy)] border-b border-gray-200">
                  <Check className="w-4 h-4 text-success" />
                  Inclusions
                </div>
                <ul className={`p-4 space-y-2 ${styles.fontSize}`}>
                  {inclusionsList.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-700">
                      <Check className="w-4 h-4 text-success mt-0.5 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* Exclusions */}
            {exclusionsList.length > 0 && (
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                <div className="px-4 py-2 font-semibold flex items-center gap-2 text-[var(--navy)] border-b border-gray-200">
                  <X className="w-4 h-4 text-error" />
                  Exclusions
                </div>
                <ul className={`p-4 space-y-2 ${styles.fontSize}`}>
                  {exclusionsList.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-gray-700">
                      <X className="w-4 h-4 text-error mt-0.5 shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InclusionsExclusions;
